import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import ProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffCatalogProductTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import useTypedSelector from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import { PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Tab } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffCatalogProductDrawerProps {
  open: boolean
  handleClose: () => void
}

export function TariffCatalogProductDrawer({
  open,
  handleClose
}: Readonly<TariffCatalogProductDrawerProps>) {
  const { id: tariffId, catalogId } = useParams()
  const tariffRootUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const baseUrl = `${tariffRootUrl}/${tariffId}/sales-channels/${catalogId}`
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useSelectedTariff(tariffId)
  const { confirmForUnsavedChanges } = useUnsavedChanges()

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffCatalogProductTabType.DETAILS

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffCatalogProductTabType
  ) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      handleClose()
    })
  }

  const isSingleProductLoading = singleProductLoading === 'pending'

  return (
    <DrawerRight open={open} onClose={handleDrawerClose}>
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Sales channel product tab list`}
        >
          <Tab label={t`Product details`} value={TariffCatalogProductTabType.DETAILS} />
          <Tab label={t`Pricing`} value={TariffCatalogProductTabType.PRICING} />
          <Tab label={t`Availability`} value={TariffCatalogProductTabType.AVAILABILITY} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.PRICING}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && <ProductPricing product={singleProduct} />}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.AVAILABILITY}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
              />
            )}
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}

export default TariffCatalogProductDrawer
